import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useStateMachine } from "little-state-machine";
import Answer from "./Answer";
import {
  updateQuestion,
  previousQuestion,
  nextQuestion,
  setScore,
  setCurrentValues,
} from "../utils/updateActions";
import { useForm } from "react-hook-form";
import {useCalculations} from '../hooks';

const Question = ({ appId, setShouldShowResults, theme }) => {

  const { actions, state } = useStateMachine({
    updateQuestion,
    previousQuestion,
    nextQuestion,
    setScore,
    setCurrentValues,
  });
  // const total = useCalculationTotal(state[appId]);
  const {result, total, calculationTypes} = useCalculations(state[appId]);
  const [questionId, setQuestionId] = useState(state[appId]?.currentQuestion);
  const [question, setQuestion] = useState(
    state[appId]?.data.questions[questionId]
  );
  const [questionsStarted, setQuestionsStarted] = useState([]);
  const [questionsFinished, setQuestionsFinished] = useState([]);
  const [testFinished, setTestFinished] = useState(false);

  const [testStarted, setTestStarted] = useState(false);

  const { register, getValues, reset } = useForm({
    mode: "onChange",
    defaultValues: { answer: state[appId]?.currentValues[questionId] },
  });

  useEffect(() => {
    /* Here we track when the user leaves the page */
    window.onbeforeunload = () => {
      if(!testFinished) {
        const stepNumber = !questionsStarted.includes(state[appId].currentQuestion)
                            ? state[appId].currentQuestion + 1
                            : Math.max(...questionsStarted) + 1

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          'event': 'webform_self_test',
          'action': 'leave',
          'step_number': `${stepNumber}`,
          'steps_total': `${state[appId].data.questions.length}`
        });
      }
    }
  });

  useEffect(() => {
    if (state[appId]) setQuestionId(state[appId]?.currentQuestion);
  }, [state[appId]?.currentQuestion]);

  useEffect(() => {
    if (state[appId]) {
      setQuestion(state[appId]?.data.questions[questionId]);
      const answerId = "answer" + questionId;
      reset({
        [answerId]: Object.values(state[appId]?.currentValues)[questionId] || 0,
      });
    }
  }, [questionId]);

  const handleOnChange = (e) => {

    if(e) {

      const text = question?.options[e?.target?.getAttribute('index')]?.question_option
      const result_text = question?.options[e?.target?.getAttribute('index')]?.answer
      /* Here we track when a question is started */
      // if(!questionsStarted.includes(state[appId].currentQuestion)) {
      //   setQuestionsStarted([...questionsStarted, state[appId].currentQuestion]);
      //   window.dataLayer = window.dataLayer || [];
      //   window.dataLayer.push({
      //     'event': 'webform_self_test',
      //     'action': 'started',
      //     'step_number': `${state[appId]?.currentQuestion + 1}`,
      //     'steps_total': `${state[appId].data.questions.length}`
      //   });
      // }

      const answer = getValues()["answer" + questionId];

      actions.setCurrentValues({ appId: appId, value: { [questionId]: {answer:answer, text: text, result_text: result_text} }});
    }
  };

  const getScoreButtonDisabled = () => {
    return questionId !== state[appId]?.data.questions.length - 1;
  };

  const getNextButtonDisabled = () => {
    return state[appId]?.currentValues[state[appId]?.currentQuestion] ? true : false;
  }

  const onGetPrevious = () => {
    handleOnChange();
    if (questionId > 0) actions.previousQuestion(appId);
  };

  const onGetNext = () => {
    handleOnChange();

    // Track the start of the test
    if (!testStarted) {
      setTestStarted(true);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        'event': 'selftest_start',
        'name': state[appId].data.node_title,
      });
    }

    // Old event for tracking the progress of the test
    /* Here we track when an answer is submitted successfully */
    const testValues = state[appId].currentValues;
    if(!Object.keys(testValues).length === 0 ||
        state[appId].currentValues[state[appId].currentQuestion] &&
        !questionsFinished.includes(state[appId].currentQuestion)) {

      setQuestionsFinished([...questionsFinished, state[appId].currentQuestion])

      pushProgressDataLayer();

      // window.dataLayer = window.dataLayer || [];
      // window.dataLayer.push({
      //   'event': 'webform_self_test',
      //   'action': 'successful',
      //   'step_number': `${state[appId]?.currentQuestion + 1}`,
      //   'steps_total': `${state[appId].data.questions.length}`
      // });
    }

    if (questionId < state[appId]?.data.questions.length - 1)
      actions.nextQuestion(appId);
    };

  const onGetScore = () => {
    /* Here we track when an answer is submitted successfully / test submitted */
    const testValues = state[appId].currentValues;
        
    if(!Object.keys(testValues).length === 0 ||
        state[appId].currentValues[state[appId].currentQuestion] &&
        !questionsFinished.includes(state[appId].currentQuestion)) {

      setQuestionsFinished([...questionsFinished, state[appId].currentQuestion])
      setTestFinished(true);
      setShouldShowResults(true);

      pushProgressDataLayer();

      // New event for submitting the test
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        'event': 'selftest_complete',
        'name': state[appId].data.node_title,
        'score': total,
      });

      // Previous dataLayer event
      // window.dataLayer = window.dataLayer || [];
      // window.dataLayer.push({
      //   'event': 'webform_self_test',
      //   'action': 'successful',
      //   'step_number': `${state[appId]?.currentQuestion + 1}`,
      //   'steps_total': `${state[appId].data.questions.length}`
      // });
    }

    // TODO: For debug info

    if (state[appId]?.data.debug) {
      console.info("================ Data ================");
      console.info('Group calculation types => ', calculationTypes);
      console.info('Result for groups => ', result);
      console.info('Result total => ', total);
      console.info("================ ▼ ================");
    }
    // console.log('total', total)
    // TODO: Add average formula!
    actions.setScore({ score: {'group': result, 'total': total}, appId: appId });
  };

  const pushProgressDataLayer = () => {
    const titleElem = document.createElement('div')
    titleElem.innerHTML = state[appId].data.questions[state[appId].currentQuestion].title
    const titleDiv = titleElem.querySelector('div')

    // Track the progress of the test
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event': 'selftest_progress',
      'name': state[appId].data.node_title,
      'question': titleDiv.textContent,
      'answer': state[appId].currentValues[state[appId].currentQuestion]?.text || '',
      'step': state[appId].currentQuestion + 1,
      'total_steps': state[appId].data.questions.length,
    });
  }

  return (
    <div>
      <div>
        <span className="text-lg">
          {questionId + 1}/{state[appId]?.data.questions.length}
        </span>
        <h3 className="pt-6-resp mb-0 pb-6 lg:pb-8" dangerouslySetInnerHTML={{ __html: question?.title }}></h3>
      </div>
      <form key={appId} onChange={(e) => handleOnChange(e)}>
        {question?.options.map((option, answerId) => {
          const answerKey = "answer" + answerId;
          return (
            <Answer
              key={JSON.stringify(option)}
              id={appId + "," + questionId + "-" + answerKey}
              name={"answer" + questionId}
              label={option.question_option}
              value={option.score}
              register={register}
              index={answerId}
            />
          );
        })}
        <input
          name={"answer" + questionId}
          id={"answerHidden"}
          hidden
          value={0}
          type="radio"
        />
        <sw-flex x-space="4">
          {questionId > 0 && (
            <div>
              <sw-button { ...(theme === "dark" ? {theme: theme} : {outlined: true})} onClick={onGetPrevious}>
                {window.Drupal?.t("Go Back") || "Go Back"}
              </sw-button>
            </div>
          )}
          {getScoreButtonDisabled() && (
            <div>
              <sw-button {...(getNextButtonDisabled() ? {} : { disabled: true, class: "pointer-events-none" })} onClick={onGetNext}>
                {window.Drupal?.t("Next") || "Next"}
              </sw-button>
            </div>
          )}
          {!getScoreButtonDisabled() && (
            <div>
              <sw-button
                onClick={onGetScore}
                {...(getNextButtonDisabled() ? {} : { disabled: true, class: "pointer-events-none" })}
              >
                {window.Drupal?.t("Get Score") || "Get Score"}
              </sw-button>
            </div>
          )}

        </sw-flex>
      </form>
    </div>
  );
};

Question.defaultProps = {
  appId: 10,
};

Question.propTypes = {
  appId: PropTypes.string.isRequired,
};

export default Question;
